import { uniq } from 'lodash';
import React from 'react';

/**
 * Type of createContext function.
 */
export type CreateContext = <C>(initilisedContext: C) => React.Context<C>;

/**
 * Create a react context and initialises it with the context passed as param
 * @param {C} initilisedContext - Initial context values.
 * @returns {React.Context<C>}
 */
export const createContext: CreateContext = initilisedContext => React.createContext(initilisedContext);

/**
 * Type of getWithContextHOC function.
 */
export type GetWithContextHOC = <C>(context: React.Context<C>, defaultOptions: (keyof C)[]) =>
<RC>(
  Component: React.ComponentType<RC & C>,
  options: (keyof C)[]
) => React.ComponentType<RC>;

/**
 * Returns the HOC to wrap the react component and inject the context properties.
 * @type {C} - Context.
 * @param {React.Context<C>} context - Existing context.
 * @param {Array} defaultOptions - Default context options.
 *
 * @returns {GetWithContextHOC}  - Context HOC.
 */
export const getWithContextHOC = <C,>(context: React.Context<C>, defaultOptions: (keyof C)[] = []) => 
  /**
   * @type {RC} - React component props without context props.
   * @type {CP} - Context props of the component.
   * @param {React.ComponentType<RC & CP>} Component - Component to wrap with the consumer.
   * @param {Array<keyof C>} options - Context props to inject into the component.
   *
   * @returns {React.ComponentType<RC>} - Wrapped component.
   */
  <RC, CP extends Partial<C>>(
    Component: React.ComponentType<RC & CP>,
    options: (keyof C)[] = defaultOptions
  ): React.ComponentType<RC> => class WithContext extends React.PureComponent<RC> {
    public render(): JSX.Element {
      const contextKeys = uniq(options);

      return (
        <context.Consumer>
          {(contextProps: C) => {
            // Gets the context props to inject into the component.
            const contextPropsToInject: Partial<CP> = contextKeys.reduce((tmpProps, option) => {
              /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
              tmpProps[option] = contextProps[option] as any;
              return tmpProps;
            }, {} as Partial<CP>);

            return (
              /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
              <Component {...contextPropsToInject} {...this.props as any} />
            );
          }}
        </context.Consumer>
      );
    }
  }
;
